import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ClientAuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within ClientAuthProvider');
  }
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    
    if (token) {
      // Verify token and load client data
      loadClientData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const loadClientData = async (token) => {
    try {
      const response = await axios.get(`${API}/clients/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { renewed_token, ...clientInfo } = response.data;
      // Auto-renew token if returned
      if (renewed_token) {
        localStorage.setItem('client_token', renewed_token);
      }
      setClient(clientInfo);
      setIsAuthenticated(true);
    } catch (error) {
      // Only clear token on explicit 401 (expired/invalid), not on network errors
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('client_token');
        setIsAuthenticated(false);
        setClient(null);
      } else {
        // Network error or server error — keep the token, assume still logged in
        setIsAuthenticated(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (firstName, lastName, email, phone, password) => {
    try {
      const response = await axios.post(`${API}/clients/register`, {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        password
      });
      
      const { token, ...clientData } = response.data;
      
      localStorage.setItem('client_token', token);
      setClient(clientData);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Errore durante la registrazione' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/clients/login`, { email, password });
      const { token, ...clientData } = response.data;
      
      localStorage.setItem('client_token', token);
      setClient(clientData);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login fallito' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('client_token');
    setIsAuthenticated(false);
    setClient(null);
  };

  const updateProfile = async (updateData) => {
    try {
      const token = localStorage.getItem('client_token');
      const response = await axios.put(`${API}/clients/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Errore nell\'aggiornamento del profilo'
      };
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const refreshClientData = async () => {
    const token = localStorage.getItem('client_token');
    if (token) {
      await loadClientData(token);
    }
  };

  return (
    <ClientAuthContext.Provider 
      value={{ 
        isAuthenticated, 
        client, 
        loading, 
        register, 
        login, 
        logout, 
        updateProfile,
        getAuthHeaders,
        refreshClientData
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
};
